import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    MoreHoriz as MoreIcon,
    Notifications as NotificationIcon
} from '@mui/icons-material';

interface ServiceItemProps {
    driver: string;
    vehiclePlate: string;
    time: string;
    status: 'active' | 'completed';
    onNotify?: () => void;
    onMoreOptions?: () => void;
}

interface RecentServicesCardProps {
    date?: string;
    activeServices: ServiceItemProps[];
    completedServices: ServiceItemProps[];
    className?: string;
    maxHeight?: string | number;
}

const ServiceItem: React.FC<ServiceItemProps> = ({
    driver,
    vehiclePlate,
    time,
    status,
    onNotify,
    onMoreOptions
}) => {
    const theme = useTheme();
    const isActive = status === 'active';

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 1.5,
                mb: 1.5,
                backgroundColor: isActive
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${isActive
                    ? 'rgba(25, 118, 210, 0.12)'
                    : theme.palette.divider}`,
            }}
        >
            <Box>
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        fontSize: '1.1rem',
                        mb: 0.5
                    }}
                >
                    {driver} - {vehiclePlate}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                >
                    Hoy - {time}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isActive && (
                    <IconButton
                        size="medium"
                        onClick={onNotify}
                        sx={{ color: 'primary.main' }}
                    >
                        <NotificationIcon />
                    </IconButton>
                )}

                <IconButton
                    size="medium"
                    onClick={onMoreOptions}
                    sx={{ color: isActive ? 'primary.main' : 'action.disabled' }}
                >
                    <MoreIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

const RecentServicesCard: React.FC<RecentServicesCardProps> = ({
    date = `Hoy, ${new Date().toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    })}`,
    activeServices,
    completedServices,
    className,
    maxHeight = { xs: '400px', sm: '450px', md: '500px' }
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Card
            className={className}
            sx={{
                borderRadius: 3,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
                width: '100%',
                height: 'auto',
                maxHeight: maxHeight,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <CardContent
                sx={{
                    p: 3,
                    pb: '0 !important',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexShrink: 0
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: 'text.primary'
                        }}
                    >
                        Ultimos Servicios
                    </Typography>

                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            py: 1,
                            px: 2
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {date}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        pr: 1,

                        '&::-webkit-scrollbar': {
                            width: '6px',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: theme.palette.background.default,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.mode === 'light'
                                ? 'rgba(0,0,0,0.2)'
                                : 'rgba(255,255,255,0.2)',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: theme.palette.mode === 'light'
                                ? 'rgba(0,0,0,0.3)'
                                : 'rgba(255,255,255,0.3)',
                        },
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 500,
                                mb: 2,
                                color: 'text.secondary',
                                position: 'sticky',
                                top: 0,
                                backgroundColor: theme.palette.background.paper,
                                pt: 1,
                                pb: 1,
                                zIndex: 1
                            }}
                        >
                            En curso
                        </Typography>

                        {activeServices.map((service, index) => (
                            <ServiceItem
                                key={`active-${index}`}
                                {...service}
                            />
                        ))}
                    </Box>

                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 500,
                                mb: 2,
                                color: 'text.secondary',
                                position: 'sticky',
                                top: 0,
                                backgroundColor: theme.palette.background.paper,
                                pt: 1,
                                pb: 1,
                                zIndex: 1
                            }}
                        >
                            Finalizadas
                        </Typography>

                        {completedServices.map((service, index) => (
                            <ServiceItem
                                key={`completed-${index}`}
                                {...service}
                            />
                        ))}

                        <Box sx={{ height: '10px' }} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default RecentServicesCard;